<template>
  <CustomModal ref="modal" title="Rattacher des articles" class="assign-articles-modal">
    <div class="container">
      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <p v-if="order" class="intro">Articles à affecter au <strong>dossier N°{{ order.number }}</strong><template v-if="order.title"> - {{ order.title }}</template>.</p>
        <p v-else class="intro">Choisissez le dossier, puis la quantité à lui affecter.</p>
        <p class="intro">La quantité est prélevée sur le stock de l'article, achat le plus ancien d'abord : le coût suit la quantité, sans toucher aux montants des factures d'achat. Au-delà du stock, elle est enregistrée « à approvisionner » et se rattache aux achats à leur arrivée.</p>

        <CustomSelectField v-if="!order" :content=inputs.orderId />

        <div v-if="rows.length > 6" class="search-box">
          <span class="fas fa-magnifying-glass"></span>
          <input v-model="query" type="search" placeholder="Article, référence..." />
        </div>

        <ul v-if="visibleRows.length > 0" class="rows">
          <li v-for="row in visibleRows" :key="row.articleId" class="row" :class="{filled: quantityOf(row) > 0}">
            <div class="row-main">
              <strong :title="row.name">{{ row.name }}</strong>
              <span class="muted"><span :class="{negative: row.inStockQuantity < 0}">{{ formatQuantity(row.inStockQuantity, row.unit) }} en stock</span><template v-if="row.inStockQuantity > 0"> · {{ formatAmount(unitCost(row)) }} l'unité en moyenne</template></span>
              <span v-if="shortfall(row) > 0" class="shortfall">dont {{ formatQuantity(shortfall(row), row.unit) }} au-delà du stock : à approvisionner</span>
            </div>
            <div class="row-quantity">
              <input type="number" min="0" step="any" v-model="row.quantity" placeholder="0" :class="{error: row.error}" />
              <span v-if="row.unit" class="suffix">{{ row.unit }}</span>
              <button v-if="row.inStockQuantity > 0" type="button" class="all" title="Affecter tout le stock de l'article" @click="row.quantity = row.inStockQuantity">Tout</button>
            </div>
          </li>
        </ul>
        <p v-else class="empty">{{ emptyText }}</p>

        <p v-if="selectedCount > 0" class="hint">{{ selectedCount }} article{{ selectedCount > 1 ? 's' : '' }} · environ {{ formatAmount(estimate) }} HT au prix moyen du stock (le coût exact dépend des achats prélevés). Ce qui dépasse le stock n'a pas de coût tant qu'il n'est pas acheté.</p>
        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=submitParams />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
        </div>
      </CustomForm>
    </div>
  </CustomModal>
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faMagnifyingGlass} from "@fortawesome/free-solid-svg-icons";
import {NotificationService} from "@vues/v-common";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import {ArticleController} from "@/purchase/controllers/ArticleController";
import {LogisticsController} from "@/purchase/controllers/LogisticsController";
import {formatAmount, formatQuantity, round2} from "@/purchase/utils";
library.add(faMagnifyingGlass);

const normalize = text => String(text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Modale d'affectation d'articles du catalogue à un dossier, sans choisir l'achat : c'est l'autre façon de rattacher des achats à un dossier,
// à côté du rattachement d'un achat complet (facture de services, achat entier). La quantité est prélevée sur le stock de l'article (ms-purchase).
// openModal({order}) : depuis un dossier (order porte id, number et title). openModal({orderValues, articleId}) : depuis un article, le dossier se choisit.
// callback est appelé après l'affectation.
export default {
  name: 'AssignArticlesModal',
  props: ['callback'],
  components: {CustomModal, CustomForm, CustomSelectField, CustomButton},
  data() {
    return {
      order: undefined,
      rows: [],            // articles en stock, avec la quantité saisie
      query: '',
      isLoading: false,
      isSaving: false,
      inputs: {
        orderId: {label: 'Dossier', value: undefined, type: 'select', selectValues: [], required: true},
      },
      buttons: {
        submit: {text: "Rattacher"},
        cancel: {text: "Annuler"},
      }
    };
  },
  computed: {
    visibleRows() {
      const query = normalize(this.query.trim());
      return this.rows.filter(row => !query || normalize(`${row.name} ${row.reference ?? ''}`).includes(query));
    },
    selectedRows() {
      return this.rows.filter(row => this.quantityOf(row) > 0);
    },
    selectedCount() {
      return this.selectedRows.length;
    },
    // Au prix moyen du stock de l'article, sur la part que le stock couvre : les achats prélevés peuvent coûter plus ou moins
    estimate() {
      return round2(this.selectedRows.reduce((sum, row) => sum + (this.quantityOf(row) - this.shortfall(row)) * this.unitCost(row), 0));
    },
    emptyText() {
      return this.isLoading ? 'Chargement...' : "Aucun article au catalogue. Ajoutez-en depuis l'onglet Articles ou depuis une ligne d'achat.";
    },
    submitParams() {
      return {...this.buttons.submit, disabled: this.isSaving || this.selectedCount === 0};
    },
  },
  methods: {
    formatAmount,
    formatQuantity,
    quantityOf(row) {
      return Number(String(row.quantity ?? '').replace(',', '.')) || 0;
    },
    // Part de la quantité saisie que le stock ne couvre pas
    shortfall(row) {
      return Math.max(0, round2(this.quantityOf(row) - Math.max(0, row.inStockQuantity)));
    },
    unitCost(row) {
      return row.inStockQuantity > 0 ? row.stockValueHt / row.inStockQuantity : 0;
    },
    async openModal({order, orderValues, articleId} = {}) {
      this.order = order;
      this.query = '';
      this.rows = [];
      this.inputs.orderId.value = undefined;
      this.inputs.orderId.error = false;
      this.inputs.orderId.selectValues = orderValues ?? [];
      this.$refs.modal.openModal();

      this.isLoading = true;
      const stats = await ArticleController.getStats();
      this.isLoading = false;
      this.rows = (stats && !stats.errors ? stats : [])
          .filter(article => !article.deactivated && (!articleId || article.articleId === articleId))
          .map(article => ({
            articleId: article.articleId, name: article.name, reference: article.reference, unit: article.unit,
            inStockQuantity: article.inStockQuantity, stockValueHt: article.stockValueHt, quantity: '', error: false,
          }));
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    async submitForm(event) {
      this.preventNativeSubmit(event);
      this.rows.forEach(row => { row.error = false; });

      const orderId = this.order ? this.order.id : this.inputs.orderId.value;
      if (!orderId) {
        this.inputs.orderId.error = true;
        (new NotificationService).addNotification("danger", "Le dossier n'est pas renseigné");
        return;
      }
      if (this.selectedCount === 0) {
        (new NotificationService).addNotification("danger", "Indiquez la quantité d'au moins un article");
        return;
      }
      const body = {orderId, items: this.selectedRows.map(row => ({articleId: row.articleId, quantity: this.quantityOf(row)}))};
      this.isSaving = true;
      const res = await this.$refs.form.submitForm(() => LogisticsController.assignArticles(body));
      this.isSaving = false;
      if (!res.errors) {
        const missing = this.selectedRows.filter(row => this.shortfall(row) > 0).length;
        (new NotificationService).addNotification("success", (this.selectedCount > 1 ? "Articles rattachés au dossier" : "Article rattaché au dossier") + (missing > 0 ? " (une partie reste à approvisionner)" : ""));
        this.closeModal(true);
        if (this.callback) this.callback();
      }
    },
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.container
  display flex
  flex-direction column
  gap 10px

  form
    display flex
    flex-direction column
    gap 8px

    .intro
      margin 0
      font-size 13.5px
      color #4a5670

    .muted
      color #6f7a93
      font-size 12.5px

    .hint
      margin 0
      font-size 12.5px
      color #8a93a8

    .negative
      color #c0392b
      font-weight 600

    .shortfall
      font-size 12px
      font-weight 600
      color #9a6b00

    .empty
      margin 0
      padding 22px 12px
      border 1px dashed #d6dcea
      border-radius 12px
      text-align center
      font-size 13.5px
      color #8a93a8

    .search-box
      display flex
      align-items center
      gap 8px
      padding 0 12px
      height 38px
      background CTRL_BG
      border 1px solid CTRL_BORDER
      border-radius CTRL_RADIUS

      &:focus-within
        background CTRL_BG_FOCUS
        border-color CTRL_FOCUS
        box-shadow 0 0 0 3px CTRL_FOCUS_RING

      span
        color CTRL_PLACEHOLDER
        font-size 13px

      input
        flex 1
        min-width 0
        border none
        outline none
        background transparent
        font-size 14px

    .rows
      max-height 320px
      margin 0
      padding 4px
      list-style none
      overflow-y auto
      border 1px solid #e3e7ef
      border-radius 12px

    .row
      display flex
      align-items center
      justify-content space-between
      gap 12px
      padding 9px 12px
      border-radius 9px

      &:hover
        background #f6f8fd

      &.filled
        background #e8edf8

    .row-main
      display flex
      flex-direction column
      min-width 0

      strong
        overflow hidden
        white-space nowrap
        text-overflow ellipsis
        color #2b3550

    .row-quantity
      display flex
      align-items center
      gap 6px
      flex 0 0 auto

      input
        width 92px
        height 34px
        padding 0 8px
        border 1px solid CTRL_BORDER
        border-radius CTRL_RADIUS
        background CTRL_BG
        text-align right
        font-size 14px
        font-variant-numeric tabular-nums

        &:focus
          outline none
          border-color CTRL_FOCUS
          box-shadow 0 0 0 3px CTRL_FOCUS_RING

        &.error
          border-color #d9534f

      .suffix
        min-width 22px
        font-size 12.5px
        color #8a93a8

      .all
        height 28px
        padding 0 8px
        border 1px solid #e0e5f0
        border-radius 7px
        background white
        color #4a5670
        font-size 12px
        font-weight 600
        cursor pointer

        &:hover
          background #eef2fb
          color THEME_COLOR_PRIMARY

    // Le bouton principal est en premier dans le DOM (action par défaut de la touche Entrée),
    // l'ordre visuel est inversé : [Annuler] [Rattacher]
    .button-container
      display flex
      flex-direction row-reverse
      justify-content flex-start
      gap 10px
      margin-top 8px

      .button
        min-width 110px
</style>

<style lang="stylus">
.assign-articles-modal
  > .modal
    width 560px !important
    max-width 100%
    height auto !important
    max-height 92vh
    overflow-y auto
</style>
